import type {
  AdminPrivacyRequest,
  AdminPrivacyRequestSummary,
  PrivacyRequestPatch,
  PrivacyRequestStatus,
  PrivacyRequestType,
} from '@/admin/adminApi';
import type { BadgeTone } from '@/ui/kit';
import type { ProgressTone } from '@/ui/charts';

/**
 * Vocabulaire et regles d'affichage du registre des demandes RGPD.
 *
 * Module **pur**, teste sans rendu. Une regle le traverse : **aucun chiffre n'est
 * calcule ici**. Retard, jours restants, avancement, effectifs et delai moyen sont
 * ceux du serveur ; ce module les met en phrases, et ecrit « — » quand ils manquent.
 */

export const ABSENT = '—';

/** Plafond de la note de cloture (`PrivacyRequestService::MAX_NOTE_LENGTH`). */
export const NOTE_LIMIT = 500;

export const TYPE_LABELS: Readonly<Record<PrivacyRequestType, string>> = {
  access: 'Accès',
  rectification: 'Rectification',
  erasure: 'Effacement',
  portability: 'Portabilité',
  objection: 'Opposition',
};

/** Ordre de la liste de saisie : les droits les plus demandes d'abord. */
export const TYPE_ORDER: readonly PrivacyRequestType[] = ['access', 'erasure', 'rectification', 'portability', 'objection'];

export const TYPE_TONES: Readonly<Record<PrivacyRequestType, BadgeTone>> = {
  access: 'info',
  rectification: 'neutral',
  erasure: 'danger',
  portability: 'info',
  objection: 'warning',
};

export const STATUS_LABELS: Readonly<Record<PrivacyRequestStatus, string>> = {
  received: 'À traiter',
  in_progress: 'En cours',
  closed: 'Clôturée',
};

export const STATUS_TONES: Readonly<Record<PrivacyRequestStatus, BadgeTone>> = {
  received: 'warning',
  in_progress: 'info',
  closed: 'neutral',
};

/** Un export ne repond a une demande qu'en partie : voir `ExportLimitation`. */
export const typeNeedsExport = (type: PrivacyRequestType): boolean => type === 'access' || type === 'portability';

const DAY_FORMAT = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
const NUMBER_FORMAT = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });

/**
 * Jour civil **UTC**, comme le serveur le compte (`LegalDeadline`) : un jour lu
 * dans le fuseau du navigateur pourrait differer de celui de l'echeance.
 */
export const formatDay = (iso: string | null): string => {
  if (iso === null || iso === '') {
    return ABSENT;
  }

  const date = new Date(iso);

  return Number.isNaN(date.getTime()) ? ABSENT : DAY_FORMAT.format(date);
};

const plural = (count: number, one: string, many: string): string => `${NUMBER_FORMAT.format(count)} ${count > 1 ? many : one}`;

/** « dans 26 j », « demain », « aujourd'hui », « en retard de 3 j ». `null` : ni jours ni retard a dire. */
export const daysLabel = (days: number | null): string => {
  if (days === null) {
    return ABSENT;
  }

  if (days < 0) {
    return `en retard de ${NUMBER_FORMAT.format(-days)} j`;
  }

  if (days === 0) {
    return 'aujourd’hui';
  }

  return days === 1 ? 'demain' : `dans ${NUMBER_FORMAT.format(days)} j`;
};

/** Phrase de la colonne « Echeance » : ouverte, ou close dans / hors delai. */
export const deadlineLabel = (request: AdminPrivacyRequest): string => {
  if (request.status !== 'closed') {
    return daysLabel(request.daysRemaining);
  }

  if (request.closedOnTime === null) {
    return 'clôturée';
  }

  return request.closedOnTime ? 'clôturée dans le délai' : 'clôturée hors délai';
};

/** Teinte de la barre. La phrase dit toujours l'etat : la teinte ne le porte jamais seule. */
export const deadlineTone = (request: AdminPrivacyRequest): ProgressTone => {
  if (request.overdue) {
    return 'critical';
  }

  return (request.elapsedPercent ?? 0) >= 75 ? 'warning' : 'success';
};

/** « 14 sur 14 », ou « — » quand il n'y a rien sur quoi rapporter : jamais un pourcentage seul. */
export const ratioLabel = (part: number, whole: number): string =>
  whole <= 0 ? ABSENT : `${NUMBER_FORMAT.format(part)} sur ${NUMBER_FORMAT.format(whole)}`;

export const meanDelayLabel = (days: number | null): string => (days === null ? ABSENT : `${NUMBER_FORMAT.format(days)} j`);

/** Tuile « Ouvertes » : les effacements et les retards, seulement quand il y en a. */
export const openCaption = (summary: AdminPrivacyRequestSummary): string => {
  if (summary.open === 0) {
    return 'Aucune demande à traiter';
  }

  const parts: string[] = [];

  if (summary.overdue > 0) {
    parts.push(`${NUMBER_FORMAT.format(summary.overdue)} en retard`);
  }

  if (summary.openByType.erasure > 0) {
    parts.push(plural(summary.openByType.erasure, 'effacement', 'effacements'));
  }

  return parts.length === 0 ? 'Aucune en retard' : `dont ${parts.join(' · ')}`;
};

export const nearestCaption = (summary: AdminPrivacyRequestSummary): string =>
  summary.nearestDueAt === null ? 'Aucune demande ouverte' : `Échéance le ${formatDay(summary.nearestDueAt)}`;

/** Passages de statut que le serveur accepte depuis l'etat courant (jamais en arriere). */
export const statusOptionsFor = (current: PrivacyRequestStatus): readonly PrivacyRequestStatus[] => {
  switch (current) {
    case 'received':
      return ['received', 'in_progress', 'closed'];
    case 'in_progress':
      return ['in_progress', 'closed'];
    case 'closed':
      return [];
  }
};

// ---------------------------------------------------------------------------
// Saisie
// ---------------------------------------------------------------------------

export interface NewRequestForm {
  readonly type: PrivacyRequestType;
  readonly email: string;
  /** `AAAA-MM-JJ`, tel que le champ date le rend. */
  readonly receivedOn: string;
}

export interface NewRequestErrors {
  readonly email?: string;
  readonly receivedOn?: string;
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;
const DAY_SHAPE = /^\d{4}-\d{2}-\d{2}$/u;

/**
 * Premier filtre de saisie : le serveur reste le seul juge (422).
 *
 * « Aujourd'hui » se lit en **UTC**, comme le serveur lit une date seule (minuit
 * UTC) : se fier au fuseau du navigateur laisserait passer, apres minuit a Paris,
 * une date que le serveur refuserait.
 */
export const validateNewRequest = (form: NewRequestForm, now: Date): NewRequestErrors => {
  const errors: { email?: string; receivedOn?: string } = {};

  if (!EMAIL_SHAPE.test(form.email.trim())) {
    errors.email = 'Saisissez une adresse e-mail valide.';
  }

  if (!DAY_SHAPE.test(form.receivedOn)) {
    errors.receivedOn = 'Indiquez la date de réception.';
  } else if (form.receivedOn > now.toISOString().slice(0, 10)) {
    errors.receivedOn = 'La date de réception ne peut pas être dans le futur.';
  }

  return errors;
};

export const hasErrors = (errors: object): boolean => Object.keys(errors).length > 0;

// ---------------------------------------------------------------------------
// Evolution
// ---------------------------------------------------------------------------

export interface EditForm {
  readonly status: PrivacyRequestStatus;
  /** Identifiant du responsable, `''` pour aucun. */
  readonly assigneeUserId: string;
  readonly note: string;
}

export const editFormOf = (request: AdminPrivacyRequest): EditForm => ({
  status: request.status,
  assigneeUserId: request.assigneeUserId ?? '',
  note: request.outcomeNote ?? '',
});

/**
 * Corps du `PATCH` : **seulement ce qui a change**. Envoyer le tout reecrirait
 * un responsable ou une note qu'un autre operateur vient de poser.
 */
export const buildPatch = (request: AdminPrivacyRequest, form: EditForm): PrivacyRequestPatch => {
  const initial = editFormOf(request);
  const patch: { status?: PrivacyRequestStatus; assigneeUserId?: string | null; outcomeNote?: string | null } = {};

  if (form.status !== initial.status) {
    patch.status = form.status;
  }

  if (form.assigneeUserId !== initial.assigneeUserId) {
    patch.assigneeUserId = form.assigneeUserId === '' ? null : form.assigneeUserId;
  }

  if (form.note.trim() !== initial.note.trim()) {
    patch.outcomeNote = form.note.trim() === '' ? null : form.note.trim();
  }

  return patch;
};

export const noteError = (note: string): string | undefined =>
  note.length > NOTE_LIMIT ? `La note dépasse ${String(NOTE_LIMIT)} caractères (${String(note.length)}).` : undefined;

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

export const pageCaption = (total: number, page: number, perPage: number): string => {
  if (total <= 0) {
    return 'Aucune demande';
  }

  const pages = Math.max(1, Math.ceil(total / perPage));

  return `${plural(total, 'demande', 'demandes')} · page ${String(page)} sur ${String(pages)}`;
};

// ---------------------------------------------------------------------------
// Registre des acces
// ---------------------------------------------------------------------------

export const accessLine = (item: {
  readonly requestCount: number;
  readonly distinctAccountCount: number;
  readonly roleChanges: number;
}): string => {
  const parts = [plural(item.distinctAccountCount, 'compte consulté', 'comptes consultés'), plural(item.requestCount, 'requête', 'requêtes')];

  if (item.roleChanges > 0) {
    parts.push(plural(item.roleChanges, 'rôle modifié', 'rôles modifiés'));
  }

  return parts.join(' · ');
};

export const CONSOLE_LABEL = 'Console';
